import React, { useState, useEffect } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { listItems, getDownloadUrl, getUploadUrl, deleteItem, confirmUpload } from '../services/fileMetadataApi';
import './Lyrics.css';

// The lyrics folder ID in DynamoDB (under public folder)
const LYRICS_FOLDER_ID = '494ce8f88283df5a';

const Lyrics = () => {
  const [lyrics, setLyrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewLyricForm, setShowNewLyricForm] = useState(false);
  const [newLyricTitle, setNewLyricTitle] = useState('');
  const [newLyricContent, setNewLyricContent] = useState('');
  const [editingLyric, setEditingLyric] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [username, setUsername] = useState('');
  const [expandedLyrics, setExpandedLyrics] = useState({});

  useEffect(() => {
    fetchUsername();
    fetchLyrics();
  }, []);

  const fetchUsername = async () => {
    try {
      const user = await getCurrentUser();
      setUsername(user.username);
    } catch (error) {
      console.error('Error fetching username:', error);
    }
  };

  const fetchLyrics = async () => {
    try {
      setLoading(true);
      
      // List all items in the lyrics folder directly
      const result = await listItems(LYRICS_FOLDER_ID);
      
      // Filter for .txt files only
      const lyricFiles = result.items.filter(item => 
        item.type === 'file' && item.name.endsWith('.txt')
      );

      // Fetch content for each lyric file
      const lyricsWithContent = await Promise.all(
        lyricFiles.map(async (file) => {
          try {
            const { downloadUrl } = await getDownloadUrl(file.id);
            
            const response = await fetch(downloadUrl);
            const content = await response.text();
            
            // Extract metadata from the filename
            const filename = file.name;
            const nameWithoutExtension = filename.replace('.txt', '');
            const parts = nameWithoutExtension.split('__');
            
            let title = nameWithoutExtension;
            let author = 'Unknown';
            let date = file.createdAt || new Date().toISOString();
            
            if (parts.length >= 3) {
              title = parts[0].replace(/_/g, ' ');
              author = parts[1].replace(/_/g, ' ');
              date = new Date(parseInt(parts[2])).toISOString();
            } else if (parts.length >= 1) {
              title = parts[0].replace(/_/g, ' ');
            }
            
            return {
              id: file.id,
              key: file.s3Key || `lyrics/${filename}`,
              title,
              author,
              date,
              content,
              lastModified: file.updatedAt || file.createdAt || new Date().toISOString()
            };
          } catch (error) {
            console.error(`Error fetching content for ${file.name}:`, error);
            return {
              id: file.id,
              key: file.s3Key || file.name,
              title: file.name.replace('.txt', '').replace(/_/g, ' '),
              author: 'Unknown',
              content: 'Error loading content',
              lastModified: file.updatedAt || file.createdAt || new Date().toISOString()
            };
          }
        })
      );

      // Sort by last modified date (newest first)
      lyricsWithContent.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      
      setLyrics(lyricsWithContent);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching lyrics:', error);
      setError('Failed to load lyrics. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateLyric = async (e) => {
    e.preventDefault();
    
    if (!newLyricTitle.trim()) {
      setError('Please enter a title for your lyrics.');
      return;
    }
    
    try {
      const timestamp = Date.now();
      const formattedTitle = newLyricTitle.trim().replace(/\s+/g, '_');
      const formattedUsername = username.replace(/\s+/g, '_');
      
      // Format: Title__Author__Timestamp.txt
      const fileName = `${formattedTitle}__${formattedUsername}__${timestamp}.txt`;
      
      // Get upload URL from the file metadata API
      const { uploadUrl, itemId } = await getUploadUrl({
        fileName,
        mimeType: 'text/plain',
        size: new Blob([newLyricContent]).size,
        parentFolderId: LYRICS_FOLDER_ID
      });
      
      // Upload the content
      await fetch(uploadUrl, {
        method: 'PUT',
        body: newLyricContent,
        headers: {
          'Content-Type': 'text/plain'
        }
      });
      
      // Confirm the upload
      await confirmUpload(itemId);
      
      // Reset form
      setNewLyricTitle('');
      setNewLyricContent('');
      setShowNewLyricForm(false);
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error creating lyric:', error);
      setError('Failed to create lyric. Please try again.');
    }
  };

  const handleUpdateLyric = async () => {
    if (!editingLyric) return;
    
    try {
      // For updates, we delete the old file and create a new one with the same name
      const fileName = editingLyric.key.split('/').pop();
      
      const { uploadUrl, itemId } = await getUploadUrl({
        fileName,
        mimeType: 'text/plain',
        size: new Blob([editContent]).size,
        parentFolderId: LYRICS_FOLDER_ID
      });
      
      // Delete the old file
      await deleteItem(editingLyric.id);
      
      // Upload the new content
      await fetch(uploadUrl, {
        method: 'PUT',
        body: editContent,
        headers: {
          'Content-Type': 'text/plain'
        }
      });
      
      // Confirm the upload
      await confirmUpload(itemId);
      
      // Reset editing state
      setEditingLyric(null);
      setEditContent('');
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error updating lyric:', error);
      setError('Failed to update lyric. Please try again.');
    }
  };

  const handleDeleteLyric = async (lyric) => {
    if (!window.confirm('Are you sure you want to delete these lyrics? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteItem(lyric.id);
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error deleting lyric:', error);
      setError('Failed to delete lyric. Please try again.');
    }
  };

  const startEditing = (lyric) => {
    setEditingLyric(lyric);
    setEditContent(lyric.content);
  };

  const cancelEditing = () => {
    setEditingLyric(null);
    setEditContent('');
  };

  const toggleLyricExpansion = (id) => {
    setExpandedLyrics(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="lyrics-container">
      <h2>Band Lyrics</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button className="dismiss-button" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      <div className="lyrics-actions">
        <button 
          className="new-lyric-button"
          onClick={() => setShowNewLyricForm(!showNewLyricForm)}
        >
          {showNewLyricForm ? 'Cancel' : 'Add New Lyrics'}
        </button>
      </div>
      
      {showNewLyricForm && (
        <div className="lyric-form">
          <h3>Create New Lyrics</h3>
          <form onSubmit={handleCreateLyric}>
            <div className="form-group">
              <label htmlFor="lyric-title">Song Title</label>
              <input
                id="lyric-title"
                type="text"
                value={newLyricTitle}
                onChange={(e) => setNewLyricTitle(e.target.value)}
                placeholder="Enter song title"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lyric-content">Lyrics</label>
              <textarea
                id="lyric-content"
                value={newLyricContent}
                onChange={(e) => setNewLyricContent(e.target.value)}
                placeholder="Enter lyrics here..."
                rows={10}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="save-button">Save Lyrics</button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowNewLyricForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading ? (
        <div className="loading-lyrics">Loading lyrics...</div>
      ) : lyrics.length === 0 ? (
        <div className="no-lyrics">
          <p>No lyrics have been added yet.</p>
          <p>Click "Add New Lyrics" to create the first song lyrics.</p>
        </div>
      ) : (
        <div className="lyrics-list">
          {lyrics.map((lyric) => (
            <div key={lyric.id} className="lyric-card">
              {editingLyric && editingLyric.id === lyric.id ? (
                <div className="lyric-edit">
                  <h3>{lyric.title}</h3>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={15}
                  />
                  <div className="edit-actions">
                    <button onClick={handleUpdateLyric} className="save-button">Save Changes</button>
                    <button onClick={cancelEditing} className="cancel-button">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="lyric-header" onClick={() => toggleLyricExpansion(lyric.id)}>
                    <div className="lyric-title-row">
                      <h3>{lyric.title}</h3>
                      <span className="expand-icon">
                        {expandedLyrics[lyric.id] ? '▼' : '►'}
                      </span>
                    </div>
                    <div className="lyric-meta">
                      <span className="lyric-author">By: {lyric.author}</span>
                      <span className="lyric-date">Last updated: {formatDate(lyric.lastModified)}</span>
                    </div>
                  </div>
                  
                  {expandedLyrics[lyric.id] && (
                    <>
                      <div className="lyric-content">
                        <pre>{lyric.content}</pre>
                      </div>
                      <div className="lyric-actions">
                        <button onClick={() => startEditing(lyric)} className="edit-button">Edit</button>
                        <button onClick={() => handleDeleteLyric(lyric)} className="delete-button">Delete</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      
    </div>
  );
};

export default Lyrics;
